import React from 'react';
import type { Dayjs } from 'dayjs';
import { FormMessage, ScheduleKind } from '../../types';
import { AnnualScheduleRow } from './AnnualScheduleRow';
import { ScheduleRow } from './ScheduleRow';
import { scheduleOptionsFor } from './scheduleOptions';

interface SchedulingSectionProps {
  message: FormMessage;
  onChange: (next: Partial<FormMessage>) => void;
  scheduleKind: ScheduleKind;
  /** 1-based message position; the first message has nothing to be relative to. */
  position: number;
  disabled?: boolean;
  /** Lower bound for the absolute-time picker. */
  minDateTime?: Dayjs | null;
  /** Enrollment-only: this message opens the follow-up section. */
  isFirstEnrollmentScheduledMessage?: boolean;
  scheduleTimeError?: string;
}

/** Join and enrollment centre the delay editor instead of stacking it. */
const centredDelay = { outerSx: { alignItems: 'center', marginTop: '13px' } };
const spacedDivider = { marginTop: '20px' };

/**
 * The schedule controls for one message. Every kind but annual draws the same
 * row and differs only in its option list and a handful of props.
 */
export const SchedulingSection: React.FC<SchedulingSectionProps> = (props) => {
  const { scheduleKind, position, isFirstEnrollmentScheduledMessage } = props;
  const options = scheduleOptionsFor(scheduleKind, {
    position,
    isFirstFollowUp: !!isFirstEnrollmentScheduledMessage,
    enrollmentSection: props.message.enrollmentSection,
  });

  switch (scheduleKind) {
    case 'reminder':
      return (
        <ScheduleRow
          {...props}
          options={options}
          delayPeriod="d"
          delayInput={{
            inputWidth: '80px',
            trailingLabel: 'Before Calendar Event',
            alignItems: 'center',
          }}
          compact
        />
      );

    case 'annual':
      return <AnnualScheduleRow {...props} />;

    case 'flyer':
      return (
        <ScheduleRow
          {...props}
          options={options}
          delayPeriod="m"
          // The flyer renderer never adopted the unit from a saved delay.
          syncPeriodOnMount={false}
          timeControl="timeOfDay"
          dividerSx={spacedDivider}
        />
      );

    case 'generic':
      return (
        <ScheduleRow
          {...props}
          options={options}
          delayPeriod="m"
          timeControl="dateTime"
          dividerSx={spacedDivider}
        />
      );

    // Join offers no absolute time, but still renders the picker for a message
    // saved with one.
    case 'join':
    case 'enrollment':
      return (
        <ScheduleRow
          {...props}
          options={options}
          delayPeriod="m"
          timeControl="dateTime"
          delayInput={centredDelay}
          showTimeHelperText={scheduleKind === 'enrollment'}
        />
      );

    // A recurring campaign keeps its schedule in the campaign settings.
    case 'recurring':
    default:
      return null;
  }
};
